import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatNumber, formatPrice } from '../../utils/helpers';
import './HyperliquidPositionsTable.css';

/**
 * Shorten wallet address: 0x1234...5678
 */
const shortenAddress = (address) => {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

/**
 * Format leverage: 10.5x → 10x, 1.0x → 1x
 */
const formatLeverage = (leverage) => {
  if (!leverage) return '-';
  return `${Math.round(leverage)}x`;
};

/**
 * Calculate tier for position based on value
 * Tier 3: $50M - $100M
 * Tier 4: $100M+
 */
const getTier = (dollarSize) => {
  if (dollarSize < 50_000_000) return null;
  if (dollarSize < 100_000_000) return 3;
  return 4;
};

/**
 * Position row component
 */
const PositionRow = ({ position, onClick, t }) => {
  const isProfitable = position.unrealizedPnl >= 0;
  const pnlClass = isProfitable ? 'success' : 'danger';

  const pnlPercentage = position.returnOnEquity != null
    ? (position.returnOnEquity * 100).toFixed(2)
    : 'N/A';

  const tier = getTier(position.positionValue || 0);
  const tierClass = tier ? `tier-${tier}` : '';
  const volumeClass = position.side === 'long' ? 'success' : 'danger';

  return (
    <div
      className={`position-row ${tierClass}`.trim()}
      onClick={() => onClick(position)}
    >
      <div className={`volume ${volumeClass}`} style={{ display: 'none' }}></div>

      <div className="position-user">
        <span className="user-address" title={position.user}>
          {shortenAddress(position.user)}
        </span>
      </div>

      <div className="position-liq-price">
        <span className="liq-price-text">${formatPrice(position.liquidationPrice)}</span>
      </div>

      <div className="position-symbol">
        <span className="symbol-text">{position.symbol}</span>
      </div>

      <div className={`position-side ${position.side === 'long' ? 'long' : 'short'}`}>
        <span className="side-badge">{position.side === 'long' ? t('hyperliquid.long') : t('hyperliquid.short')}</span>
      </div>

      <div className="position-size">
        <span className="size-value">${formatNumber(position.positionValue)}</span>
        <span className="size-label">
          {((position.positionValue || 0) / (position.markPrice || 1)).toFixed(3)} {position.symbol}
        </span>
      </div>

      <div className="position-leverage">
        <span className="leverage-value">{formatLeverage(position.leverage)}</span>
      </div>

      <div className={`position-pnl ${pnlClass}`}>
        <span className="pnl-value">
          {isProfitable ? '+' : ''}${formatNumber(position.unrealizedPnl)}
        </span>
        <span className="pnl-label">
          {pnlPercentage !== 'N/A' && (isProfitable ? '+' : '')}{pnlPercentage}{pnlPercentage !== 'N/A' && '%'}
        </span>
      </div>

      <div className="position-prices">
        <div className="price-row">
          <span className="price-label">{t('hyperliquid.entry')}:</span>
          <span className="price-value">{formatPrice(position.entryPrice)}</span>
        </div>
        <div className="price-row">
          <span className="price-label">{t('hyperliquid.mark')}:</span>
          <span className="price-value">{formatPrice(position.markPrice)}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Hyperliquid Positions Table Component
 * Shows real-time positions from Hyperliquid traders
 */
function HyperliquidPositionsTable({ positions, loading, isConnected, hasActiveFilters, onResetFilters }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [lastUpdateText, setLastUpdateText] = useState('-');

  // Sort state - load from localStorage
  const [sortBy, setSortBy] = useState(() => {
    const saved = localStorage.getItem('hlPositionsSortBy');
    if (saved === 'newest' || saved === 'oldest') return 'volume-high';
    return saved || 'volume-high';
  });

  // Save sort preference to localStorage
  useEffect(() => {
    localStorage.setItem('hlPositionsSortBy', sortBy);
  }, [sortBy]);

  // Sort positions based on selected option
  const sortedPositions = useMemo(() => {
    const sorted = [...positions];

    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => {
          const aTime = typeof (a.openedAt || a.timestamp) === 'number' ? (a.openedAt || a.timestamp) : new Date(a.openedAt || a.timestamp).getTime();
          const bTime = typeof (b.openedAt || b.timestamp) === 'number' ? (b.openedAt || b.timestamp) : new Date(b.openedAt || b.timestamp).getTime();
          return bTime - aTime;
        });

      case 'oldest':
        return sorted.sort((a, b) => {
          const aTime = typeof (a.openedAt || a.timestamp) === 'number' ? (a.openedAt || a.timestamp) : new Date(a.openedAt || a.timestamp).getTime();
          const bTime = typeof (b.openedAt || b.timestamp) === 'number' ? (b.openedAt || b.timestamp) : new Date(b.openedAt || b.timestamp).getTime();
          return aTime - bTime;
        });

      case 'volume-high':
        return sorted.sort((a, b) => (b.positionValue || 0) - (a.positionValue || 0));

      case 'volume-low':
        return sorted.sort((a, b) => (a.positionValue || 0) - (b.positionValue || 0));

      case 'pnl-high':
        return sorted.sort((a, b) => (b.unrealizedPnl || 0) - (a.unrealizedPnl || 0));

      case 'pnl-low':
        return sorted.sort((a, b) => (a.unrealizedPnl || 0) - (b.unrealizedPnl || 0));

      case 'leverage-high':
        return sorted.sort((a, b) => (b.leverage || 0) - (a.leverage || 0));

      default:
        return sorted;
    }
  }, [positions, sortBy]);

  // Handle position click - navigate to wallet profile
  const handlePositionClick = (position) => {
    navigate(`/hyperliquid/wallet/${position.user}`);
  };

  // Update last update time when positions change
  useEffect(() => {
    if (sortedPositions.length > 0) {
      setLastUpdateTime(Date.now());
    }
  }, [sortedPositions]);

  // Update "last update" text every second
  useEffect(() => {
    if (!lastUpdateTime) {
      setLastUpdateText('-');
      return;
    }

    const updateText = () => {
      const diffSec = Math.floor((Date.now() - lastUpdateTime) / 1000);
      if (diffSec < 60) {
        setLastUpdateText(t('time.secondsAgo', { count: diffSec }));
      } else if (diffSec < 3600) {
        setLastUpdateText(t('time.minutesAgo', { count: Math.floor(diffSec / 60) }));
      } else {
        setLastUpdateText(t('time.hoursAgo', { count: Math.floor(diffSec / 3600) }));
      }
    };

    updateText();
    const interval = setInterval(updateText, 1000);
    return () => clearInterval(interval);
  }, [lastUpdateTime, t]);

  if (loading) {
    return (
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <div className="card-title mb-0">
            <p>{t('hyperliquid.title')}</p>
          </div>
        </div>
        <div className="card-body scroll">
          <div className="text-center py-5">
            <div className="spinner-border text-light" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card hyperliquid-positions-card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div className="card-title mb-0">
          <div className="info-hover">
            <p className="title-with-tooltip">{t('hyperliquid.title')}</p>
            <div className="tooltip-card">
              <div className="card-body">
                <h5>{t('hyperliquid.tooltipTitle')}</h5>
                <span className="divider"></span>
                <p>{t('hyperliquid.tooltipDesc1')}</p>
                <p>{t('hyperliquid.tooltipDesc2')}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="action d-flex align-items-center gap-2">
          <small className="text-muted last-update-text" style={{ fontSize: '10px' }}>
            {t('hyperliquid.lastUpdate')}: {lastUpdateText}
          </small>
          <select
            className="form-select form-select-sm bg-dark text-light border-secondary"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{ width: 'auto', fontSize: '12px' }}
          >
            <option value="volume-high">{t('hyperliquid.volumeHighToLow')}</option>
            <option value="volume-low">{t('hyperliquid.volumeLowToHigh')}</option>
            <option value="pnl-high">{t('hyperliquid.pnlHighToLow')}</option>
            <option value="pnl-low">{t('hyperliquid.pnlLowToHigh')}</option>
            <option value="leverage-high">{t('hyperliquid.leverageHighToLow')}</option>
          </select>
          <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
            <span className="status-dot"></span>
            <span className="status-text">{isConnected ? t('hyperliquid.live') : t('hyperliquid.disconnected')}</span>
          </div>
        </div>
      </div>

      {/* Table Header */}
      <div className="positions-table-header">
        <div className="header-cell header-user">{t('hyperliquid.user')}</div>
        <div className="header-cell header-liq-price">{t('hyperliquid.liqPrice')}</div>
        <div className="header-cell header-symbol">{t('hyperliquid.symbol')}</div>
        <div className="header-cell header-side">{t('hyperliquid.side')}</div>
        <div className="header-cell header-size">{t('hyperliquid.volume')}</div>
        <div className="header-cell header-leverage">{t('hyperliquid.leverage')}</div>
        <div className="header-cell header-pnl">{t('hyperliquid.pnl')}</div>
        <div className="header-cell header-prices">{t('hyperliquid.prices')}</div>
      </div>

      <div className="card-body scroll">
        {sortedPositions.length === 0 ? (
          <div className="no-records-wrapper">
            {isConnected && hasActiveFilters ? (
              <div className="text-center py-5">
                <p className="text-muted mb-3">{t('hyperliquid.noPositionsFiltered')}</p>
                <button className="btn btn-secondary btn-sm" onClick={onResetFilters}>
                  {t('hyperliquid.resetFilters')}
                </button>
              </div>
            ) : (
              <p className="text-center text-muted py-5">
                {isConnected ? t('hyperliquid.noPositions') : t('hyperliquid.connecting')}
              </p>
            )}
          </div>
        ) : (
          sortedPositions.map((position) => (
            <PositionRow
              key={`${position.user}-${position.symbol}`}
              position={position}
              onClick={handlePositionClick}
              t={t}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default HyperliquidPositionsTable;
